import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { SkeletonAdminSettings } from '../../components/Skeleton';
import { invalidateScoring } from '../../lib/scoring';

export default function AdminGlobalSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appName, setAppName] = useState('');
  const [weightAttendance, setWeightAttendance] = useState('');
  const [weightCaseload, setWeightCaseload] = useState('');
  const [weightRetention, setWeightRetention] = useState('');
  const [redZone, setRedZone] = useState('');
  const [summaryRecipients, setSummaryRecipients] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    api.admin.settings()
      .then((data) => {
        setSettings(data);
        setAppName(data.app_name || '');
        setWeightAttendance(data.cscore_weight_attendance ?? '');
        setWeightCaseload(data.cscore_weight_caseload ?? '');
        setWeightRetention(data.cscore_weight_retention ?? '');
        setRedZone(data.cscore_red_zone_threshold ?? '');
        setSummaryRecipients((data.monthly_summary_recipients || []).join('\n'));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.admin.updateSettings({
        app_name: appName,
        cscore_weight_attendance: Number(weightAttendance),
        cscore_weight_caseload: Number(weightCaseload),
        cscore_weight_retention: Number(weightRetention),
        cscore_red_zone_threshold: Number(redZone),
        monthly_summary_recipients: summaryRecipients.split(/[\s,]+/).filter(Boolean),
      });
      // Every page caches the threshold for the session, so a save has to
      // clear it or the flags stay on the old number until a full reload.
      invalidateScoring();
    } catch (err) {
      console.error('Save failed:', err);
      setSaveError(err.message || 'Save failed');
    }
    setSaving(false);
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const { sent, to } = await api.admin.previewMonthlySummary();
      setPreviewResult(sent > 0 ? `Preview sent to ${to}.` : `Could not send the preview to ${to}.`);
    } catch (err) {
      setPreviewResult(err.message || 'Could not send the preview.');
    }
    setPreviewing(false);
  };

  if (loading) {
    return <SkeletonAdminSettings />;
  }

  return (
    <div>
      <div className="flex items-start sm:items-center justify-between mb-6 gap-4">
        <h1 className="text-2xl md:text-[32px] font-semibold text-text">Global Settings</h1>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 disabled:opacity-50 transition-colors flex-shrink-0"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* App Name */}
      <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 mb-6">
        <h2 className="text-lg font-bold text-text mb-4">App Name</h2>
        <input
          type="text"
          value={appName}
          onChange={(e) => setAppName(e.target.value)}
          className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
        />
      </div>

      {/* App Logos */}
      <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 mb-6">
        <h2 className="text-lg font-bold text-text mb-4">App Logos</h2>
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <div className="flex-1">
            <p className="text-sm font-medium text-text mb-2">Primary Logo</p>
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt="Primary Logo" className="max-h-[80px] object-contain" />
            ) : (
              <div className="h-[80px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                <p className="text-sm text-gray-400">No logo uploaded</p>
              </div>
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-text mb-2">Secondary Logo</p>
            {settings?.logo_secondary_url ? (
              <img src={settings.logo_secondary_url} alt="Secondary Logo" className="max-h-[80px] object-contain" />
            ) : (
              <div className="h-[80px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                <p className="text-sm text-gray-400">No logo uploaded</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Weights */}
      <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 mb-6">
        <h2 className="text-lg font-bold text-text mb-4">Custom Weights</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          <div>
            <label className="block text-sm font-medium text-text mb-1">Attendance</label>
            <input
              type="number"
              value={weightAttendance}
              onChange={(e) => setWeightAttendance(e.target.value)}
              className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Caseload</label>
            <input
              type="number"
              value={weightCaseload}
              onChange={(e) => setWeightCaseload(e.target.value)}
              className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Retention</label>
            <input
              type="number"
              value={weightRetention}
              onChange={(e) => setWeightRetention(e.target.value)}
              className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
            />
          </div>
        </div>
      </div>

      {/* Red Zone */}
      <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 mb-6">
        <h2 className="text-lg font-bold text-text mb-1">Red Zone</h2>
        <p className="text-sm text-gray-500 mb-4">
          Clinicians scoring at or below this are marked on every dashboard and clinic list.
        </p>
        <div className="max-w-[200px]">
          <label htmlFor="red-zone" className="block text-sm font-medium text-text mb-1">Threshold</label>
          <input
            id="red-zone"
            type="number"
            min="0"
            max="1000"
            step="1"
            value={redZone}
            onChange={(e) => setRedZone(e.target.value)}
            className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
          />
        </div>
        {saveError && <p className="text-sm text-destructive mt-3">{saveError}</p>}
      </div>

      {/* Monthly Summary Email */}
      <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 mb-6">
        <h2 className="text-lg font-bold text-text mb-1">Monthly Summary Email</h2>
        <p className="text-sm text-gray-500 mb-4">
          Sent on the 5th of each month for the month before. One address per line. Leave empty to send nothing.
        </p>
        <label htmlFor="summary-recipients" className="block text-sm font-medium text-text mb-1">Recipients</label>
        <textarea
          id="summary-recipients"
          rows={3}
          value={summaryRecipients}
          onChange={(e) => setSummaryRecipients(e.target.value)}
          className="w-full max-w-[480px] border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
        />
        <div className="flex items-center gap-3 mt-3">
          <button
            onClick={handlePreview}
            disabled={previewing}
            className="px-4 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary-10 disabled:opacity-50 transition-colors"
          >
            {previewing ? 'Sending...' : 'Send me a preview'}
          </button>
          {previewResult && <p className="text-sm text-gray-500">{previewResult}</p>}
        </div>
      </div>
    </div>
  );
}
